import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Calendar, Target, Dumbbell, Clock, ChevronDown, ChevronUp, Info, Loader2, ArrowUp, ArrowDown, Minus, Plus, GripVertical, Eye, PlayCircle } from 'lucide-react';
import { aiApi, workoutApi } from '@/services/api';
import { Card, Button } from '@/components/common';
import { useWorkoutStore } from '@/store/workoutStore';
import { useUIStore } from '@/store/uiStore';
import type { AiWorkoutPlan, GeneratedWorkoutPlan, GeneratedExercise, WorkoutGoal } from '@/types';

const GOAL_LABELS: Record<string, string> = {
  increase_weight: 'Súly növelése',
  increase_reps: 'Ismétlésszám növelése',
  increase_time: 'Idő növelése',
  general: 'Általános fejlődés',
};

const GOAL_OPTIONS: WorkoutGoal[] = ['increase_weight', 'increase_reps', 'increase_time', 'general'];

const formatDate = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('hu-HU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    weekday: 'short',
  });
};

interface MuscleGroupConfigItem {
  name: string;
  exerciseCount: number;
  order: number;
}

interface EquipmentItem {
  id: string;
  name: string;
  type: string;
}

const WorkoutBuilder = () => {
  const navigate = useNavigate();
  const showToast = useUIStore((s) => s.showToast);
  const { loadActiveWorkout } = useWorkoutStore();
  const [targetDate, setTargetDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [goal, setGoal] = useState<WorkoutGoal>('general');
  const [generating, setGenerating] = useState(false);
  const [applying, setApplying] = useState(false);
  const [plans, setPlans] = useState<AiWorkoutPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [expandedPlanId, setExpandedPlanId] = useState<string | null>(null);
  const [expandedReasoningId, setExpandedReasoningId] = useState<string | null>(null);


  // New state: muscle group config (order + exercise count)
  const [muscleGroupConfig, setMuscleGroupConfig] = useState<MuscleGroupConfigItem[]>([]);

  // Exercise type filter: machine or bodyweight
  const [exerciseType, setExerciseType] = useState<'machine' | 'bodyweight'>('machine');

  // Muscle groups per category
  const [machineMuscleGroups, setMachineMuscleGroups] = useState<string[]>([]);
  const [bodyweightMuscleGroups, setBodyweightMuscleGroups] = useState<string[]>([]);
  const [selectedMuscleGroups, setSelectedMuscleGroups] = useState<Set<string>>(new Set());

  const [result, setResult] = useState<{
    plan: AiWorkoutPlan;
    usage: {
      model: string;
      promptTokens: number;
      completionTokens: number;
      totalTokens: number;
      costUsd: number;
      durationMs: number;
    };
  } | null>(null);

  useEffect(() => {
    const loadEquipment = async () => {
      try {
        const res = await aiApi.getWorkoutContext(targetDate);
        if (res.success && res.data) {
          const m = res.data.machines || [];
          const bw = res.data.bodyweightExercises || [];

          const machineGroups = new Set<string>();
          m.forEach((item: EquipmentItem) => { if (item.type) machineGroups.add(item.type); });
          setMachineMuscleGroups(Array.from(machineGroups).sort());

          const bwGroups = new Set<string>();
          bw.forEach((item: EquipmentItem) => { const g = (item as any).muscleGroup || item.type; if (g) bwGroups.add(g); });
          setBodyweightMuscleGroups(Array.from(bwGroups).sort());
        }
      } catch {
        // non-critical
      }
    };
    loadEquipment();
  }, [targetDate]);

  useEffect(() => {
    const loadPlans = async () => {
      try {
        const res = await aiApi.getWorkoutPlans();
        if (res.success && res.data?.plans) {
          setPlans(res.data.plans);
        }
      } catch (err: unknown) {
        const msg = err && typeof err === 'object' && 'message' in err ? String((err as { message: string }).message) : 'Nem sikerült betölteni a korábbi terveket';
        showToast(msg, 'error');
      } finally {
        setPlansLoading(false);
      }
    };
    loadPlans();
  }, [showToast]);

  const toggleMuscleGroup = useCallback((name: string) => {
    setSelectedMuscleGroups(prev => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
        setMuscleGroupConfig(cfg => cfg.filter(c => c.name !== name));
      } else {
        next.add(name);
        setMuscleGroupConfig(cfg => [...cfg, { name, exerciseCount: 2, order: cfg.length }]);
      }
      return next;
    });
  }, []);

  const moveMuscleGroup = useCallback((index: number, direction: -1 | 1) => {
    setMuscleGroupConfig(prev => {
      const newConfig = [...prev];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= newConfig.length) return prev;
      [newConfig[index], newConfig[targetIndex]] = [newConfig[targetIndex], newConfig[index]];
      return newConfig.map((item, i) => ({ ...item, order: i }));
    });
  }, []);

  const updateExerciseCount = useCallback((name: string, delta: number) => {
    setMuscleGroupConfig(prev =>
      prev.map(item =>
        item.name === name
          ? { ...item, exerciseCount: Math.max(1, Math.min(6, item.exerciseCount + delta)) }
          : item
      )
    );
  }, []);

  const settingsRef = useRef<HTMLDivElement>(null);

  const restoreSettingsFromPlan = useCallback((p: AiWorkoutPlan) => {
    if (p.targetDate) setTargetDate(new Date(p.targetDate).toISOString().slice(0, 10));
    if (p.goal) setGoal(p.goal as WorkoutGoal);

    const planFocus: string[] = Array.isArray(p.focusMuscleGroups) ? p.focusMuscleGroups as string[] : [];
    setSelectedMuscleGroups(new Set(planFocus));
    setMuscleGroupConfig(planFocus.map((name, i) => ({ name, exerciseCount: 2, order: i })));

    if (planFocus.length > 0) {
      const isBw = bodyweightMuscleGroups.some(bg => planFocus.includes(bg));
      setExerciseType(isBw ? 'bodyweight' : 'machine');
    }

    const gen = p.generatedPlan as GeneratedWorkoutPlan | undefined;
    if (gen) {
      setResult({
        plan: p,
        usage: {
          model: p.model ?? '-',
          promptTokens: 0,
          completionTokens: 0,
          totalTokens: p.totalTokens ?? 0,
          costUsd: p.costUsd ?? 0,
          durationMs: 0,
        },
      });
    }

    setTimeout(() => settingsRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  }, [bodyweightMuscleGroups]);

  const applyPlan = useCallback(async (exercises: GeneratedExercise[]) => {
    if (!exercises || exercises.length === 0) {
      showToast('Nincs gyakorlat az edzéstervben!', 'error');
      return;
    }

    setApplying(true);
    try {
      const activeCheck = await workoutApi.getActive();
      if (activeCheck.success && activeCheck.data?.workout) {
        showToast('Már van aktív edzés! Fejezd be előbb a jelenlegi edzést.', 'error');
        setApplying(false);
        return;
      }

      const startRes = await workoutApi.start();
      if (!startRes.success || !startRes.data?.workout) {
        throw new Error('Nem sikerült elindítani az edzést');
      }
      const workoutId = startRes.data.workout.id;

      const aiSuggestions: Record<string, { name: string; muscleGroup: string; sets: GeneratedExercise['sets']; reasoning?: string }> = {};
      for (const ex of exercises) {
        const data: { machineId?: string; bodyweightExerciseId?: string } = {};
        if (ex.machineId) data.machineId = ex.machineId;
        else if (ex.bodyweightExerciseId) data.bodyweightExerciseId = ex.bodyweightExerciseId;
        else continue;
        const addRes = await workoutApi.addExercise(workoutId, data);
        if (addRes.success && addRes.data?.exercise) {
          aiSuggestions[addRes.data.exercise.id] = {
            name: ex.name,
            muscleGroup: ex.muscleGroup,
            sets: ex.sets,
            reasoning: ex.reasoning,
          };
        }
      }

      sessionStorage.setItem('aiWorkoutSuggestions', JSON.stringify(aiSuggestions));
      await loadActiveWorkout();
      showToast('Edzésterv alkalmazva! Az első gyakorlat időmérője elindul.', 'success');
      navigate('/workout?aiApplied=true');
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'message' in err ? String((err as { message: string }).message) : 'Hiba az edzésterv alkalmazásakor';
      showToast(msg, 'error');
    } finally {
      setApplying(false);
    }
  }, [showToast, navigate, loadActiveWorkout]);

  const handleGenerate = async () => {
    setGenerating(true);
    setResult(null);
    try {
      const focusMuscleGroups = muscleGroupConfig.length > 0 ? muscleGroupConfig.map(c => c.name) : undefined;
      const res = await aiApi.generateWorkout({
        targetDate,
        goal,
        focusMuscleGroups,
        muscleGroupConfig: muscleGroupConfig.length > 0 ? muscleGroupConfig : undefined,
      });
      if (res.success && res.data) {
        setResult(res.data as typeof result);
        setPlans((prev) => [res.data!.plan as AiWorkoutPlan, ...prev]);
        showToast('Edzésterv sikeresen generálva!', 'success');
      } else {
        showToast('Hiba történt a generálás során', 'error');
      }
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'message' in err ? String((err as { message: string }).message) : 'Nem sikerült generálni az edzéstervet';
      showToast(msg, 'error');
    } finally {
      setGenerating(false);
    }
  };

  const plan = result?.plan;
  const usage = result?.usage;
  const generated = plan?.generatedPlan as GeneratedWorkoutPlan | undefined;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <Sparkles className="w-7 h-7 text-primary-600" />
          Edzés összeállító (AI)
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          AI alapú személyre szabott napi edzésterv
        </p>
      </div>

      {/* Settings Section */}
      <div ref={settingsRef}>
      <Card>
        <div className="space-y-6">
          {/* Target date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Célnap
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Goal */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Cél
            </label>
            <div className="flex flex-wrap gap-2">
              {GOAL_OPTIONS.map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setGoal(g)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    goal === g
                      ? 'bg-primary-600 text-white dark:bg-primary-500'
                      : 'border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {GOAL_LABELS[g]}
                </button>
              ))}
            </div>
          </div>

          {/* Exercise type selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Edzéstípus
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setExerciseType('machine')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  exerciseType === 'machine'
                    ? 'bg-primary-600 text-white dark:bg-primary-500'
                    : 'border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <Dumbbell className="w-4 h-4" />
                Gépes edzés
              </button>
              <button
                type="button"
                onClick={() => setExerciseType('bodyweight')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  exerciseType === 'bodyweight'
                    ? 'bg-primary-600 text-white dark:bg-primary-500'
                    : 'border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <Target className="w-4 h-4" />
                Saját súlyos
              </button>
            </div>
          </div>

          {/* Focus muscle groups with order and exercise count */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Fókusz izomcsoportok <span className="text-gray-400 font-normal">opcionális - válassz sorrendet és gyakorlatszámot</span>
            </label>
            <div className="flex flex-wrap gap-2 mb-4">
              {(exerciseType === 'machine' ? machineMuscleGroups : bodyweightMuscleGroups).map(mg => (
                <button
                  key={mg}
                  type="button"
                  onClick={() => toggleMuscleGroup(mg)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    selectedMuscleGroups.has(mg)
                      ? 'bg-primary-600 text-white dark:bg-primary-500'
                      : 'border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {mg}
                </button>
              ))}
            </div>

            {/* Ordered muscle group config */}
            {muscleGroupConfig.length > 0 && (
              <div className="space-y-2 bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Sorrend beállítása (nyilakkal) + gyakorlatszám izomcsoportonként
                </p>
                {muscleGroupConfig.map((item, idx) => (
                  <div
                    key={item.name}
                    className="flex items-center gap-3 bg-white dark:bg-gray-700 rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-600"
                  >
                    <GripVertical className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="text-sm font-medium text-primary-700 dark:text-primary-300 w-5 text-center">
                      {idx + 1}.
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 flex-1">
                      {item.name}
                    </span>

                    {/* Move up/down */}
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => moveMuscleGroup(idx, -1)}
                        disabled={idx === 0}
                        className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ArrowUp className="w-4 h-4 text-gray-500" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveMuscleGroup(idx, 1)}
                        disabled={idx === muscleGroupConfig.length - 1}
                        className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ArrowDown className="w-4 h-4 text-gray-500" />
                      </button>
                    </div>

                    {/* Exercise count */}
                    <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-600 rounded-lg px-1">
                      <button
                        type="button"
                        onClick={() => updateExerciseCount(item.name, -1)}
                        disabled={item.exerciseCount <= 1}
                        className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-500 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <Minus className="w-3.5 h-3.5 text-gray-600 dark:text-gray-300" />
                      </button>
                      <span className="text-sm font-bold text-gray-900 dark:text-gray-100 w-6 text-center">
                        {item.exerciseCount}
                      </span>
                      <button
                        type="button"
                        onClick={() => updateExerciseCount(item.name, 1)}
                        disabled={item.exerciseCount >= 6}
                        className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-500 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <Plus className="w-3.5 h-3.5 text-gray-600 dark:text-gray-300" />
                      </button>
                    </div>
                    <span className="text-xs text-gray-400 dark:text-gray-500 w-16">gyakorlat</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Button
            onClick={handleGenerate}
            disabled={generating}
            loading={generating}
            icon={<Sparkles className="w-5 h-5" />}
            size="lg"
            className="w-full sm:w-auto"
          >
            Edzésterv generálása
          </Button>
        </div>
      </Card>
      </div>

      {/* Loading State */}
      {generating && (
        <div className="flex flex-col items-center justify-center py-16">
          <Loader2 className="w-12 h-12 text-primary-600 animate-spin mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Edzésterv generálása folyamatban...</p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">Az AI dolgozik a személyre szabott terven</p>
        </div>
      )}

      {/* Result Section */}
      {!generating && result && generated && (
        <div className="space-y-6">
          {usage && (
            <div className="flex flex-wrap gap-4 px-4 py-3 rounded-lg bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm">
              <span className="flex items-center gap-1.5">
                <span className="px-2 py-0.5 rounded bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-200">
                  {usage.model}
                </span>
              </span>
              <span className="text-gray-600 dark:text-gray-400">
                {usage.totalTokens.toLocaleString()} token
              </span>
              <span className="text-gray-600 dark:text-gray-400">
                ${usage.costUsd.toFixed(4)}
              </span>
              <span className="text-gray-600 dark:text-gray-400">
                {usage.durationMs} ms
              </span>
            </div>
          )}

          <Card className="bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Összefoglaló</h3>
            <p className="text-gray-700 dark:text-gray-300">{generated.summary}</p>
            {generated.focusMuscleGroups?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {generated.focusMuscleGroups.map((mg) => (
                  <span
                    key={mg}
                    className="px-2 py-1 rounded text-xs font-medium bg-primary-100 dark:bg-primary-900/40 text-primary-800 dark:text-primary-200"
                  >
                    {mg}
                  </span>
                ))}
              </div>
            )}
          </Card>

          <Card className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Target className="w-5 h-5 text-green-600" />
              Bemelegítés ({generated.warmup?.duration ?? 0} perc)
            </h3>
            <p className="text-gray-700 dark:text-gray-300 mt-2">{generated.warmup?.description ?? '-'}</p>
          </Card>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Dumbbell className="w-5 h-5 text-primary-600" />
              Gyakorlatok
            </h3>
            {generated.exercises?.map((ex, idx) => (
              <Card key={idx}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100">{ex.name}</h4>
                    <span className="inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300">
                      {ex.muscleGroup}
                    </span>
                  </div>
                </div>

                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-600">
                        <th className="text-left py-2 text-gray-600 dark:text-gray-400">Sor</th>
                        <th className="text-left py-2 text-gray-600 dark:text-gray-400">Ism.</th>
                        <th className="text-left py-2 text-gray-600 dark:text-gray-400">Súly (kg)</th>
                        <th className="text-left py-2 text-gray-600 dark:text-gray-400">Pihenő (mp)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ex.sets?.map((s, i) => (
                        <tr key={i} className="border-b border-gray-100 dark:border-gray-700">
                          <td className="py-2 text-gray-900 dark:text-gray-100">{i + 1}</td>
                          <td className="py-2 text-gray-700 dark:text-gray-300">
                            {s.reps ?? (s.durationSeconds ? `${s.durationSeconds}s` : '-')}
                          </td>
                          <td className="py-2 text-gray-700 dark:text-gray-300">{s.weight ?? '-'}</td>
                          <td className="py-2 text-gray-700 dark:text-gray-300">{s.restSeconds ?? '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {ex.reasoning && (
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedReasoningId((prev) => (prev === `${idx}` ? null : `${idx}`))
                      }
                      className="flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      <Info className="w-4 h-4" />
                      Miért?
                      {expandedReasoningId === `${idx}` ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </button>
                    {expandedReasoningId === `${idx}` && (
                      <p className="mt-2 pl-6 text-sm text-gray-600 dark:text-gray-400 italic">
                        {ex.reasoning}
                      </p>
                    )}
                  </div>
                )}
              </Card>
            ))}
          </div>

          <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Target className="w-5 h-5 text-blue-600" />
              Levezetés ({generated.cooldown?.duration ?? 0} perc)
            </h3>
            <p className="text-gray-700 dark:text-gray-300 mt-2">{generated.cooldown?.description ?? '-'}</p>
          </Card>

          <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
            <Clock className="w-5 h-5 text-primary-600" />
            <span className="font-medium">
              Becsült időtartam: {generated.totalEstimatedDuration ?? 0} perc
            </span>
          </div>

          {generated.notes && (
            <Card>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Megjegyzések</h3>
              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{generated.notes}</p>
            </Card>
          )}

          <Button
            onClick={() => applyPlan(generated.exercises)}
            disabled={applying}
            loading={applying}
            icon={<PlayCircle className="w-5 h-5" />}
            size="lg"
            className="w-full bg-green-600 hover:bg-green-700 text-white"
          >
            {applying ? 'Alkalmazás...' : 'Edzés alkalmazása'}
          </Button>
        </div>
      )}

      {/* Previous Plans Section */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Korábbi tervek
        </h2>
        {plansLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
          </div>
        ) : plans.length === 0 ? (
          <Card>
            <p className="text-gray-500 dark:text-gray-400 text-center py-4">
              Még nincs generált edzésterv. Generálj egyet a fenti űrlappal!
            </p>
          </Card>
        ) : (
          <div className="space-y-2">
            {plans.map((p) => {
              const pg = p.generatedPlan as GeneratedWorkoutPlan | undefined;
              const isExpanded = expandedPlanId === p.id;
              return (
                <div key={p.id}>
                  <Card className="hover:border-primary-300 dark:hover:border-primary-600 transition-colors">
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => setExpandedPlanId((prev) => (prev === p.id ? null : p.id))}
                    >
                      <div className="flex items-center gap-3">
                        <Calendar className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100">
                            {formatDate(p.targetDate)}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {GOAL_LABELS[p.goal] ?? p.goal} · {p.totalTokens.toLocaleString()} token · $
                            {p.costUsd.toFixed(4)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          title="Beállítások visszatöltése"
                          onClick={(e) => { e.stopPropagation(); restoreSettingsFromPlan(p); }}
                          className="p-1.5 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/40 text-primary-600 dark:text-primary-400 transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                    {isExpanded && pg && (
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
                        {/* Token info */}
                        <div className="flex flex-wrap gap-3 text-xs">
                          <span className="px-2 py-0.5 rounded bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-200">{p.model ?? '-'}</span>
                          <span className="text-gray-500 dark:text-gray-400">{(p.totalTokens ?? 0).toLocaleString()} token</span>
                          <span className="text-gray-500 dark:text-gray-400">${(p.costUsd ?? 0).toFixed(4)}</span>
                        </div>

                        {/* Summary */}
                        {pg.summary && (
                          <p className="text-sm text-gray-700 dark:text-gray-300">{pg.summary}</p>
                        )}

                        {/* Focus muscles */}
                        {pg.focusMuscleGroups && pg.focusMuscleGroups.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {pg.focusMuscleGroups.map(mg => (
                              <span key={mg} className="px-2 py-0.5 rounded text-xs font-medium bg-primary-100 dark:bg-primary-900/40 text-primary-800 dark:text-primary-200">{mg}</span>
                            ))}
                          </div>
                        )}

                        {/* Warmup */}
                        {pg.warmup && (
                          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 border border-green-200 dark:border-green-800">
                            <p className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2 text-sm">
                              <Target className="w-4 h-4 text-green-600" />
                              Bemelegítés ({pg.warmup.duration ?? 0} perc)
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{pg.warmup.description ?? '-'}</p>
                          </div>
                        )}

                        {/* Exercises */}
                        {pg.exercises && pg.exercises.length > 0 && (
                          <div className="space-y-3">
                            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                              <Dumbbell className="w-4 h-4 text-primary-600" />
                              Gyakorlatok ({pg.exercises.length})
                            </h4>
                            {pg.exercises.map((ex, idx) => (
                              <div key={idx} className="bg-white dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                                <div className="flex items-start justify-between gap-2">
                                  <div>
                                    <span className="font-medium text-gray-900 dark:text-gray-100 text-sm">{idx + 1}. {ex.name}</span>
                                    <span className="ml-2 px-1.5 py-0.5 rounded text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300">{ex.muscleGroup}</span>
                                  </div>
                                </div>
                                {ex.sets && ex.sets.length > 0 && (
                                  <table className="w-full text-xs mt-2">
                                    <thead>
                                      <tr className="border-b border-gray-200 dark:border-gray-600">
                                        <th className="text-left py-1 text-gray-500">Ism.</th>
                                        <th className="text-left py-1 text-gray-500">Súly</th>
                                        <th className="text-left py-1 text-gray-500">Pihenő</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {ex.sets.map((s, i) => (
                                        <tr key={i} className="border-b border-gray-100 dark:border-gray-700">
                                          <td className="py-1 text-gray-700 dark:text-gray-300">{s.reps ?? (s.durationSeconds ? `${s.durationSeconds}s` : '-')}</td>
                                          <td className="py-1 text-gray-700 dark:text-gray-300">{s.weight != null ? `${s.weight} kg` : '-'}</td>
                                          <td className="py-1 text-gray-700 dark:text-gray-300">{s.restSeconds != null ? `${s.restSeconds} mp` : '-'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                                {ex.reasoning && (
                                  <div className="mt-2">
                                    <button
                                      type="button"
                                      onClick={() => setExpandedReasoningId(prev => prev === `hist-${p.id}-${idx}` ? null : `hist-${p.id}-${idx}`)}
                                      className="flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:underline"
                                    >
                                      <Info className="w-3 h-3" />
                                      Miért?
                                      {expandedReasoningId === `hist-${p.id}-${idx}` ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                    </button>
                                    {expandedReasoningId === `hist-${p.id}-${idx}` && (
                                      <p className="mt-1 pl-4 text-xs text-gray-500 dark:text-gray-400 italic">{ex.reasoning}</p>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Cooldown */}
                        {pg.cooldown && (
                          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
                            <p className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2 text-sm">
                              <Target className="w-4 h-4 text-blue-600" />
                              Levezetés ({pg.cooldown.duration ?? 0} perc)
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{pg.cooldown.description ?? '-'}</p>
                          </div>
                        )}

                        {/* Duration & notes */}
                        {pg.totalEstimatedDuration && (
                          <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                            <Clock className="w-4 h-4 text-primary-600" />
                            <span className="font-medium">Becsült időtartam: {pg.totalEstimatedDuration} perc</span>
                          </div>
                        )}
                        {pg.notes && (
                          <p className="text-xs text-gray-500 dark:text-gray-400"><strong>Megjegyzések:</strong> {pg.notes}</p>
                        )}

                        {pg.exercises && pg.exercises.length > 0 && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); applyPlan(pg.exercises); }}
                            disabled={applying}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <PlayCircle className="w-4 h-4" />
                            {applying ? 'Alkalmazás...' : 'Edzés alkalmazása'}
                          </button>
                        )}
                      </div>
                    )}
                  </Card>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkoutBuilder;
